import React, { useEffect, useState, useCallback, useRef } from "react";
import { usePermissions } from "../../../PermissionsProvider";
import { EvaluationCriteriaService } from "../../../services/EvaluationCriteriaService";
import {
  AvailableCatalog,
  EvaluationCriteria,
  EvaluationType,
} from "../../../model_gen/evaluation";
import { Loader2Icon } from "lucide-react";
import ObjectiveCriteriaCard from "./ObjectiveCriteriaCard";
import SubjectiveCriteriaCard from "./SubjectiveCriteriaCard";

interface TestRunEvaluationsProps {
  selectedCriteriaIds: number[];
  onCriteriaChange: (ids: number[]) => void;
}

const TestRunEvaluations: React.FC<TestRunEvaluationsProps> = ({
  selectedCriteriaIds,
  onCriteriaChange,
}) => {
  const { apiKey } = usePermissions();
  const [catalog, setCatalog] = useState<AvailableCatalog | null>(null);
  const [companyCriteria, setCompanyCriteria] = useState<EvaluationCriteria[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [activeFramework, setActiveFramework] = useState<string>("MAITAI_CLASSIC");
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [customScoringType, setCustomScoringType] = useState<"numeric" | "boolean">("numeric");
  const [editingCriteriaId, setEditingCriteriaId] = useState<number | null>(null);
  const [thresholdEdits, setThresholdEdits] = useState<Record<number, number>>({});
  const initialDefaultApplied = useRef(false);

  useEffect(() => {
    const sub1 = EvaluationCriteriaService.getAvailable(setLoading, apiKey).subscribe(
      (data: AvailableCatalog) => setCatalog(data),
      (err: any) => console.error("Failed to load catalog", err)
    );
    const sub2 = EvaluationCriteriaService.list(setLoading, apiKey).subscribe(
      (data: EvaluationCriteria[]) => {
        setCompanyCriteria(data);
        if (!initialDefaultApplied.current && selectedCriteriaIds.length === 0) {
          const exactMatch = data.find(
            (c) => c.type === EvaluationType.EXACT_MATCH && c.is_system
          );
          if (exactMatch?.id) {
            onCriteriaChange([exactMatch.id]);
            initialDefaultApplied.current = true;
          }
        }
      },
      (err: any) => console.error("Failed to load company criteria", err)
    );
    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [apiKey]);

  const toggleCriteria = (id: number) => {
    if (selectedCriteriaIds.includes(id)) {
      onCriteriaChange(selectedCriteriaIds.filter((x) => x !== id));
    } else {
      onCriteriaChange([...selectedCriteriaIds, id]);
    }
  };

  const resetCustomForm = () => {
    setCustomName("");
    setCustomInstructions("");
    setCustomScoringType("numeric");
    setShowCustomForm(false);
    setEditingCriteriaId(null);
  };

  const scoringTemplate = (type: "numeric" | "boolean", desc: string) => {
    if (type === "boolean") {
      return `${desc}\n\nScore 5 (Perfect): Meets the criterion.\nScore 1 (Poor): Does not meet the criterion.`;
    }
    return (
      `${desc}\n\n` +
      "Score 1 (Poor): \n" +
      "Score 2 (Fair): \n" +
      "Score 3 (Good): \n" +
      "Score 4 (Great): \n" +
      "Score 5 (Perfect): "
    );
  };

  const handleScoringTypeChange = (type: "numeric" | "boolean") => {
    setCustomScoringType(type);
    const parts = customInstructions.split("\n\n");
    const desc = parts[0] || "";
    setCustomInstructions(scoringTemplate(type, desc));
  };

  const handleCreateCustom = () => {
    if (!customName.trim() || !customInstructions.trim()) return;
    EvaluationCriteriaService.create(
      {
        name: customName,
        type: EvaluationType.LLM_JUDGE,
        framework: "CUSTOM",
        instructions: customInstructions,
        config: { scoring_type: customScoringType },
      },
      setLoading,
      apiKey
    ).subscribe(
      (created: EvaluationCriteria) => {
        setCompanyCriteria((prev) => [...prev, created]);
        if (created.id) {
          onCriteriaChange([...selectedCriteriaIds, created.id]);
        }
        resetCustomForm();
      },
      (err: any) => console.error("Failed to create criteria", err)
    );
  };

  const handleUpdateCustom = () => {
    if (!editingCriteriaId || !customName.trim() || !customInstructions.trim()) return;
    EvaluationCriteriaService.update(
      editingCriteriaId,
      {
        name: customName,
        instructions: customInstructions,
        config: { scoring_type: customScoringType },
      },
      setLoading,
      apiKey
    ).subscribe(
      (updated: EvaluationCriteria) => {
        setCompanyCriteria((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c))
        );
        resetCustomForm();
      },
      (err: any) => console.error("Failed to update criteria", err)
    );
  };

  const startEditCriteria = (crit: EvaluationCriteria) => {
    setEditingCriteriaId(crit.id!);
    setCustomName(crit.name);
    setCustomInstructions(crit.instructions || "");
    setCustomScoringType(
      (crit.config as any)?.scoring_type === "boolean" ? "boolean" : "numeric"
    );
    setShowCustomForm(true);
  };

  const saveThreshold = useCallback(
    (criteriaId: number, threshold: number) => {
      EvaluationCriteriaService.update(
        criteriaId,
        { config: { threshold } },
        () => {},
        apiKey
      ).subscribe(
        (updated: EvaluationCriteria) => {
          setCompanyCriteria((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c))
          );
        },
        (err: any) => console.error("Failed to update threshold", err)
      );
    },
    [apiKey]
  );

  if (loading && !catalog) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2Icon className="w-6 h-6 animate-spin text-maitai-green" />
      </div>
    );
  }

  const deterministicCriteria = companyCriteria.filter(
    (c) => c.type && c.type !== EvaluationType.LLM_JUDGE
  );

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-lg font-gc-med text-foreground">
          Evaluation Criteria
        </h3>
        <p className="text-sm text-muted-foreground font-gc-reg">
          Select the criteria to evaluate test run results. Exact Match is
          always included by default.
        </p>
      </div>

      <ObjectiveCriteriaCard
        deterministicCriteria={deterministicCriteria}
        selectedCriteriaIds={selectedCriteriaIds}
        catalog={catalog}
        toggleCriteria={toggleCriteria}
        thresholdEdits={thresholdEdits}
        setThresholdEdits={setThresholdEdits}
        saveThreshold={saveThreshold}
      />

      <SubjectiveCriteriaCard
        catalog={catalog}
        companyCriteria={companyCriteria}
        selectedCriteriaIds={selectedCriteriaIds}
        onCriteriaChange={onCriteriaChange}
        toggleCriteria={toggleCriteria}
        activeFramework={activeFramework}
        setActiveFramework={setActiveFramework}
        showCustomForm={showCustomForm}
        setShowCustomForm={setShowCustomForm}
        customName={customName}
        setCustomName={setCustomName}
        customInstructions={customInstructions}
        setCustomInstructions={setCustomInstructions}
        customScoringType={customScoringType}
        onScoringTypeChange={handleScoringTypeChange}
        editingCriteriaId={editingCriteriaId}
        loading={loading}
        onCreateCustom={handleCreateCustom}
        onUpdateCustom={handleUpdateCustom}
        resetCustomForm={resetCustomForm}
        startEditCriteria={startEditCriteria}
      />

      <div className="text-sm text-muted-foreground font-gc-reg">
        {selectedCriteriaIds.length} criteria selected
      </div>
    </div>
  );
};

export default TestRunEvaluations;
